import React, { useState, useEffect } from 'react';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import './locks.css';

// # Import Firebase
import { db } from '../../../../firebase/config';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useAuthStore } from '../../../../auth/state';

// # Import icons from the correct path
import cnsIcon from '../../../../general/library/step1/step1_icons/cns.png';
import endocrineIcon from '../../../../general/library/step1/step1_icons/endocrine.png';
import eyeIcon from '../../../../general/library/step1/step1_icons/eye.png';
import gitIcon from '../../../../general/library/step1/step1_icons/git.png';
import heartIcon from '../../../../general/library/step1/step1_icons/heart.png';
import kidneyIcon from '../../../../general/library/step1/step1_icons/kidney.png';
import psychiatryIcon from '../../../../general/library/step1/step1_icons/psychiatry.png';
import reproductiveIcon from '../../../../general/library/step1/step1_icons/reproductive-system.png';
import skinIcon from '../../../../general/library/step1/step1_icons/skin.png';
import anatomyIcon from '../../../../general/library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../../../../general/library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../../../../general/library/step1/step1_icons/blood.png';
import immunologyIcon from '../../../../general/library/step1/step1_icons/immunology.png';
import infectiousIcon from '../../../../general/library/step1/step1_icons/infectious.png';
import lungsIcon from '../../../../general/library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../../../../general/library/step1/step1_icons/musculoskeletal.png';
import pathologyIcon from '../../../../general/library/step1/step1_icons/pathology.png';
import pharmaIcon from '../../../../general/library/step1/step1_icons/pharma.png';
import publicHealthIcon from '../../../../general/library/step1/step1_icons/public_health.png';
import revisionIcon from '../../../../general/library/step1/step1_icons/revision.png';

// # Additional icons for Step 2
import emergencyIcon from '../../../../general/library/step1/step1_icons/emergency.png';
import healthcareIcon from '../../../../general/library/step1/step1_icons/healthcare.png';
import newbornIcon from '../../../../general/library/step1/step1_icons/newborn.png';
import qualityIcon from '../../../../general/library/step1/step1_icons/quality.png';
import statisticsIcon from '../../../../general/library/step1/step1_icons/statistics.png';
import surgeryIcon from '../../../../general/library/step1/step1_icons/surgery.png';
import wellPatientIcon from '../../../../general/library/step1/step1_icons/well_patient.png';

const ChapterPopup = ({ chapter, lockData, onClose, onSave }) => {
  const [editUrl, setEditUrl] = useState(lockData?.current_url || '');

  const handleSave = () => {
    onSave(editUrl);
    onClose();
  };

  return (
    <div className="tech_lcks_popup-overlay">
      <div className="tech_lcks_popup">
        <div className="tech_lcks_popup-header">
          <h2>{chapter.title}</h2>
          <button className="tech_lcks_popup-close" onClick={onClose}>&times;</button>
        </div>
        <div className="tech_lcks_popup-content">
          <div className="tech_lcks_popup-field">
            <label>Current URL:</label>
            <input
              type="text"
              value={editUrl}
              onChange={(e) => setEditUrl(e.target.value)}
              placeholder="Enter URL"
            />
          </div>
          {lockData?.last_edited_by && (
            <div className="tech_lcks_popup-info">
              <p>Last edited by: {lockData.last_edited_by}</p>
              <p>Last edited at: {lockData.last_edited_at?.toDate().toLocaleString()}</p>
              {lockData.previous_url && (
                <p>Previous URL: {lockData.previous_url}</p>
              )}
            </div>
          )}
        </div>
        <div className="tech_lcks_popup-footer">
          <button className="tech_lcks_popup-save" onClick={handleSave}>Save Changes</button>
          <button className="tech_lcks_popup-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// # URL mapping functions
const getStep1DefaultUrl = (chapterTitle) => {
  const urlMap = {
    'Biochemistry': process.env.REACT_APP_STEP1_BIOCHEMISTRY_IFRAME_URL,
    'Immunology': process.env.REACT_APP_STEP1_IMMUNOLOGY_IFRAME_URL,
    'Microbiology': process.env.REACT_APP_STEP1_MICROBIOLOGY_IFRAME_URL,
    'General Pathology': process.env.REACT_APP_STEP1_GENERAL_PATHOLOGY_IFRAME_URL,
    'Pharmacology': process.env.REACT_APP_STEP1_PHARMACOLOGY_IFRAME_URL,
    'Public Health Sciences': process.env.REACT_APP_STEP1_PUBLIC_HEALTH_SCIENCES_IFRAME_URL,
    'Anatomy & Histology': process.env.REACT_APP_STEP1_ANATOMY_HISTOLOGY_IFRAME_URL,
    'Cardiovascular': process.env.REACT_APP_STEP1_CARDIOVASCULAR_IFRAME_URL,
    'Endocrine': process.env.REACT_APP_STEP1_ENDOCRINE_IFRAME_URL,
    'Gastrointestinal': process.env.REACT_APP_STEP1_GASTROINTESTINAL_IFRAME_URL,
    'Hematology & Oncology': process.env.REACT_APP_STEP1_HEMATOLOGY_ONCOLOGY_IFRAME_URL,
    'Musculoskeletal & Connective Tissue': process.env.REACT_APP_STEP1_MUSCULOSKELETAL_CONNECTIVE_TISSUE_IFRAME_URL,
    'Dermatology': process.env.REACT_APP_STEP1_DERMATOLOGY_IFRAME_URL,
    'Neurology': process.env.REACT_APP_STEP1_NEUROLOGY_IFRAME_URL,
    'Special Senses': process.env.REACT_APP_STEP1_SPECIAL_SENSES_IFRAME_URL,
    'Psychiatry': process.env.REACT_APP_STEP1_PSYCHIATRY_IFRAME_URL,
    'Renal': process.env.REACT_APP_STEP1_RENAL_IFRAME_URL,
    'Reproductive': process.env.REACT_APP_STEP1_REPRODUCTIVE_IFRAME_URL,
    'Respiratory': process.env.REACT_APP_STEP1_RESPIRATORY_IFRAME_URL,
    'Rapid Review': process.env.REACT_APP_STEP1_RAPID_REVIEW_IFRAME_URL,
    'Sketchy Micro': process.env.REACT_APP_STEP1_SKETCHY_MICRO_IFRAME_URL
  };
  return urlMap[chapterTitle] || '';
};

const getStep2DefaultUrl = (chapterTitle) => {
  const urlMap = {
    'Cardiovascular': process.env.REACT_APP_STEP2_CARDIOVASCULAR_IFRAME_URL,
    'Respiratory & ENT': process.env.REACT_APP_STEP2_RESPIRATORY_ENT_IFRAME_URL,
    'Renal & Urology': process.env.REACT_APP_STEP2_RENAL_UROLOGY_IFRAME_URL,
    'Endocrine': process.env.REACT_APP_STEP2_ENDOCRINE_IFRAME_URL,
    'Hematology & Oncology': process.env.REACT_APP_STEP2_HEMATOLOGY_ONCOLOGY_IFRAME_URL,
    'Immunology': process.env.REACT_APP_STEP2_IMMUNOLOGY_IFRAME_URL,
    'GIT': process.env.REACT_APP_STEP2_GIT_IFRAME_URL,
    'Musculoskeletal': process.env.REACT_APP_STEP2_MUSCULOSKELETAL_IFRAME_URL,
    'Neurology': process.env.REACT_APP_STEP2_NEUROLOGY_IFRAME_URL,
    'Ophthalmology': process.env.REACT_APP_STEP2_OPHTHALMOLOGY_IFRAME_URL,
    'Psychiatry': process.env.REACT_APP_STEP2_PSYCHIATRY_IFRAME_URL,
    'Dermatology': process.env.REACT_APP_STEP2_DERMATOLOGY_IFRAME_URL,
    'Gynecology & Obstetrics': process.env.REACT_APP_STEP2_GYNECOLOGY_OBSTETRICS_IFRAME_URL
  };
  return urlMap[chapterTitle] || '';
};

const LocksBoard = () => {
  // # State
  const [activeTab, setActiveTab] = useState('step1');
  const [loading, setLoading] = useState(true);
  const [step1Locks, setStep1Locks] = useState({});
  const [step2Locks, setStep2Locks] = useState({});
  const [selectedChapter, setSelectedChapter] = useState(null);
  const { user } = useAuthStore();

  // # Function to fetch locks data
  const fetchLocksData = async () => {
    try {
      const locksRef = doc(db, 'technicals', 'locks');
      const locksDoc = await getDoc(locksRef);

      if (locksDoc.exists()) {
        const data = locksDoc.data();
        setStep1Locks(data.Step1_library || {});
        setStep2Locks(data.Step2_library || {});
      } else {
        // # Initialize with default data if document doesn't exist
        const defaultData = {
          Step1_library: {},
          Step2_library: {}
        };
        await setDoc(locksRef, defaultData);
        setStep1Locks({});
        setStep2Locks({});
      }
    } catch (error) {
      console.error('Error fetching locks data:', error);
    } finally {
      setLoading(false);
    }
  };

  // # Function to update chapter data
  const updateChapterData = async (library, chapter, updates) => {
    try {
      const locksRef = doc(db, 'technicals', 'locks');
      const currentLocks = library === 'Step1_library' ? step1Locks : step2Locks;
      
      // # Get default URL based on library and chapter
      const defaultUrl = library === 'Step1_library' 
        ? getStep1DefaultUrl(chapter.title)
        : getStep2DefaultUrl(chapter.title);
      
      // # Prepare the update data with the correct structure
      const chapterData = {
        last_edited_at: serverTimestamp(),
        last_edited_by: user?.email || 'unknown',
        current_url: updates.current_url || currentLocks[chapter.title]?.current_url || defaultUrl,
        previous_url: updates.current_url ? (currentLocks[chapter.title]?.current_url || '') : (currentLocks[chapter.title]?.previous_url || ''),
        unlocked: updates.unlocked !== undefined ? updates.unlocked : (currentLocks[chapter.title]?.unlocked ?? true)
      };

      // # Update Firestore
      await updateDoc(locksRef, {
        [`${library}.${chapter.title}`]: chapterData
      });

      // # Update local state
      if (library === 'Step1_library') {
        setStep1Locks(prev => ({
          ...prev,
          [chapter.title]: chapterData
        }));
      } else {
        setStep2Locks(prev => ({
          ...prev,
          [chapter.title]: chapterData
        }));
      }
    } catch (error) {
      console.error('Error updating chapter:', error);
    }
  };

  // # Handle toggle change
  const handleToggle = (library, chapter) => {
    const currentLocks = library === 'Step1_library' ? step1Locks : step2Locks;
    const isCurrentlyUnlocked = currentLocks[chapter.title]?.unlocked ?? true;
    updateChapterData(library, chapter, { unlocked: !isCurrentlyUnlocked });
  };

  // # Handle URL update
  const handleUrlUpdate = (library, chapter, newUrl) => {
    updateChapterData(library, chapter, { current_url: newUrl });
  };

  // # Add new function to handle bulk lock/unlock
  const handleBulkToggle = async (library, shouldUnlock) => {
    try {
      setLoading(true);
      const locksRef = doc(db, 'technicals', 'locks');
      const chapters = library === 'Step1_library' ? step1Chapters : step2Chapters;
      const updates = {};

      // # Prepare updates for all chapters
      chapters.forEach(chapter => {
        updates[`${library}.${chapter.title}`] = {
          last_edited_at: serverTimestamp(),
          last_edited_by: user?.email || 'unknown',
          current_url: library === 'Step1_library' 
            ? getStep1DefaultUrl(chapter.title) 
            : getStep2DefaultUrl(chapter.title),
          previous_url: '',
          unlocked: shouldUnlock
        };
      });

      // # Update Firestore
      await updateDoc(locksRef, updates);

      // # Update local state
      if (library === 'Step1_library') {
        setStep1Locks(prev => {
          const newState = { ...prev };
          chapters.forEach(chapter => {
            newState[chapter.title] = updates[`${library}.${chapter.title}`];
          });
          return newState;
        });
      } else {
        setStep2Locks(prev => {
          const newState = { ...prev };
          chapters.forEach(chapter => {
            newState[chapter.title] = updates[`${library}.${chapter.title}`];
          });
          return newState;
        });
      }
    } catch (error) {
      console.error('Error updating all chapters:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocksData();
  }, []);

  // # Step 1 chapters data
  const step1Chapters = [
    { id: 1, title: 'Biochemistry', icon: biochemistryIcon },
    { id: 2, title: 'Immunology', icon: immunologyIcon },
    { id: 3, title: 'Microbiology', icon: infectiousIcon },
    { id: 4, title: 'General Pathology', icon: pathologyIcon },
    { id: 5, title: 'Pharmacology', icon: pharmaIcon },
    { id: 6, title: 'Public Health Sciences', icon: publicHealthIcon },
    { id: 7, title: 'Anatomy & Histology', icon: anatomyIcon },
    { id: 8, title: 'Cardiovascular', icon: heartIcon },
    { id: 9, title: 'Endocrine', icon: endocrineIcon },
    { id: 10, title: 'Gastrointestinal', icon: gitIcon },
    { id: 11, title: 'Hematology & Oncology', icon: bloodIcon },
    { id: 12, title: 'Musculoskeletal & Connective Tissue', icon: musculoskeletalIcon },
    { id: 13, title: 'Dermatology', icon: skinIcon },
    { id: 14, title: 'Neurology', icon: cnsIcon },
    { id: 15, title: 'Special Senses', icon: eyeIcon },
    { id: 16, title: 'Psychiatry', icon: psychiatryIcon },
    { id: 17, title: 'Renal', icon: kidneyIcon },
    { id: 18, title: 'Reproductive', icon: reproductiveIcon },
    { id: 19, title: 'Respiratory', icon: lungsIcon },
    { id: 20, title: 'Rapid Review', icon: revisionIcon },
    { id: 21, title: 'Sketchy Micro', icon: infectiousIcon }
  ];

  // # Step 2 chapters data
  const step2Chapters = [
    { id: 1, title: 'Cardiovascular', icon: heartIcon },
    { id: 2, title: 'Respiratory & ENT', icon: lungsIcon },
    { id: 3, title: 'Renal & Urology', icon: kidneyIcon },
    { id: 4, title: 'Endocrine', icon: endocrineIcon },
    { id: 5, title: 'Hematology & Oncology', icon: bloodIcon },
    { id: 6, title: 'Immunology', icon: immunologyIcon },
    { id: 7, title: 'GIT', icon: gitIcon },
    { id: 8, title: 'Musculoskeletal', icon: musculoskeletalIcon },
    { id: 9, title: 'Neurology', icon: cnsIcon },
    { id: 10, title: 'Ophthalmology', icon: eyeIcon },
    { id: 11, title: 'Psychiatry', icon: psychiatryIcon },
    { id: 12, title: 'Dermatology', icon: skinIcon },
    { id: 13, title: 'Gynecology & Obstetrics', icon: reproductiveIcon }
  ];

  return (
    <div className="tech_lcks_container">
      <SharedHeader />
      
      {/* Title Box */}
      <div className="tech_lcks_title-box">
        <h1>Library Locks Management</h1>
        <p>Manage access permissions for different library sections</p>
      </div>

      {/* Tabs */}
      <div className="tech_lcks_tabs">
        <button 
          className={`tech_lcks_tab ${activeTab === 'step1' ? 'active' : ''}`}
          onClick={() => setActiveTab('step1')}
        >
          Step 1 Library
        </button>
        <button 
          className={`tech_lcks_tab ${activeTab === 'step2' ? 'active' : ''}`}
          onClick={() => setActiveTab('step2')}
        >
          Step 2 Library
        </button>
        <button 
          className={`tech_lcks_tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General Contents
        </button>
      </div>

      {/* Bulk Actions */}
      {activeTab !== 'general' && (
        <div className="tech_lcks_bulk-actions">
          <button
            className="tech_lcks_bulk-button unlock"
            onClick={() => handleBulkToggle(
              activeTab === 'step1' ? 'Step1_library' : 'Step2_library',
              true
            )}
          >
            Unlock All
          </button>
          <button
            className="tech_lcks_bulk-button lock"
            onClick={() => handleBulkToggle(
              activeTab === 'step1' ? 'Step1_library' : 'Step2_library',
              false
            )}
          >
            Lock All
          </button>
        </div>
      )}

      {/* Content Area */}
      <div className="tech_lcks_content">
        {loading ? (
          <div className="tech_lcks_loading">Loading...</div>
        ) : (
          <>
            {activeTab === 'step1' && (
              <div className="tech_lcks_grid">
                {step1Chapters.map((chapter) => (
                  <div key={chapter.id} className="tech_lcks_chapter-tile">
                    <div 
                      className="tech_lcks_chapter-icon"
                      onClick={() => setSelectedChapter({ type: 'Step1_library', data: chapter })}
                    >
                      <img src={chapter.icon} alt={chapter.title} />
                    </div>
                    <h3>{chapter.title}</h3>
                    <div className="tech_lcks_controls">
                      <label className="tech_lcks_switch">
                        <input
                          type="checkbox"
                          checked={step1Locks[chapter.title]?.unlocked ?? true}
                          onChange={() => handleToggle('Step1_library', chapter)}
                        />
                        <span className="tech_lcks_slider"></span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'step2' && (
              <div className="tech_lcks_grid">
                {step2Chapters.map((chapter) => (
                  <div key={chapter.id} className="tech_lcks_chapter-tile">
                    <div 
                      className="tech_lcks_chapter-icon"
                      onClick={() => setSelectedChapter({ type: 'Step2_library', data: chapter })}
                    >
                      <img src={chapter.icon} alt={chapter.title} />
                    </div>
                    <h3>{chapter.title}</h3>
                    <div className="tech_lcks_controls">
                      <label className="tech_lcks_switch">
                        <input
                          type="checkbox"
                          checked={step2Locks[chapter.title]?.unlocked ?? true}
                          onChange={() => handleToggle('Step2_library', chapter)}
                        />
                        <span className="tech_lcks_slider"></span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'general' && (
              <div className="tech_lcks_grid">
                <p>General content management coming soon...</p>
              </div>
            )}
          </>
        )}
      </div>

      {selectedChapter && (
        <ChapterPopup
          chapter={selectedChapter.data}
          lockData={selectedChapter.type === 'Step1_library' ? 
            step1Locks[selectedChapter.data.title] : 
            step2Locks[selectedChapter.data.title]
          }
          onClose={() => setSelectedChapter(null)}
          onSave={(newUrl) => {
            handleUrlUpdate(selectedChapter.type, selectedChapter.data, newUrl);
            setSelectedChapter(null);
          }}
        />
      )}

      <DashboardFooter />
    </div>
  );
};

export default LocksBoard;
